import { Alerta, Badge, Button, Combobox, Dialog, MontoField, Row, Select, Stack, TextArea, TextField } from "@/components";
import { formatMoneda } from "@/lib/format";
import { textoPartes } from "@/lib/presupuesto";
import type { EdicionRubro } from "./useEdicionRubro";

const usd = (v: string | null) => (v === null ? "—" : formatMoneda(v, "USD"));

/** Los Dialogs de agregar/editar y sacar un ítem del rubro (una sola vez por pantalla). */
export function DialogosItem({ e }: { e: EdicionRubro }) {
  const d = e.dialogo;
  return (
    <>
      <Dialog abierto={d?.tipo === "form"} onCambio={(abierto) => !abierto && e.cerrar()} titulo={d?.tipo === "form" && d.item ? "Editar ítem" : "Agregar ítem"}>
        {d?.tipo === "form" && (
          <form onSubmit={(ev) => void e.guardar(ev)}>
            <Stack espacio={4}>
              <TextArea etiqueta="Descripción" filas={3} value={e.form.descripcion} onChange={(ev) => e.cambiar("descripcion", ev.target.value)} error={e.errores.descripcion} />
              <div className="grid gap-3 sm:grid-cols-2">
                <Select
                  etiqueta="Unidad"
                  opciones={e.opcionesUnidad}
                  valor={e.form.unidad}
                  onCambio={(v) => e.cambiar("unidad", v)}
                  onCrear={(texto) => e.cambiar("unidad", texto.toLowerCase())}
                  placeholder="gl, m2, un…"
                  error={e.errores.unidad}
                />
                <TextField etiqueta="Cantidad" inputMode="decimal" value={e.form.cantidad} onChange={(ev) => e.cambiar("cantidad", ev.target.value)} error={e.errores.cantidad} />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <MontoField etiqueta="Precio unitario de materiales" valor={e.form.materiales} onCambio={(v) => e.cambiar("materiales", v)} moneda={e.precioEnUsd ? "USD" : "ARS"} deshabilitado={e.precioEnUsd} error={e.errores.materiales} />
                <MontoField etiqueta="Precio unitario de mano de obra" valor={e.form.mano_obra} onCambio={(v) => e.cambiar("mano_obra", v)} moneda={e.precioEnUsd ? "USD" : "ARS"} deshabilitado={e.precioEnUsd} error={e.errores.mano_obra} />
              </div>
              {e.precioEnUsd && <p className="text-chico text-texto-suave">Los precios de este ítem están en dólares: se pasan a pesos al cargar el tipo de cambio del presupuesto (arriba) y ahí se pueden cambiar.</p>}
              <Stack espacio={1}>
                <p className="text-texto-suave">
                  Subtotal: {e.precioEnUsd ? usd(e.subtotales.total) : `${e.subtotales.total === null ? "—" : formatMoneda(e.subtotales.total, "ARS")} · ${e.tipoCambio === null ? "USD sin tipo de cambio" : usd(e.subtotalesUsd.total)}`}
                </p>
                {e.subtotalesUsd.materiales !== null && e.subtotalesUsd.mano_obra !== null && <p className="text-chico text-texto-suave">{textoPartes(e.subtotalesUsd.materiales, e.subtotalesUsd.mano_obra)}</p>}
                {!e.precioEnUsd && e.tipoCambio !== null && <p className="text-chico text-texto-suave">{`Con el tipo de cambio del presupuesto: USD 1 = ${formatMoneda(e.tipoCambio, "ARS")}`}</p>}
              </Stack>
              <Alerta texto={e.errorGuardar} />
              <Row justificar="fin" espacio={2}>
                <Button type="button" variante="secundario" onClick={e.cerrar}>
                  Cancelar
                </Button>
                <Button type="submit" cargando={e.guardando}>
                  Guardar
                </Button>
              </Row>
            </Stack>
          </form>
        )}
      </Dialog>

      <Dialog abierto={d?.tipo === "borrar"} onCambio={(abierto) => !abierto && e.cerrar()} titulo="Sacar ítem">
        {d?.tipo === "borrar" && (
          <Stack espacio={4}>
            <p>{`¿Sacar "${d.item.descripcion}" del presupuesto?`}</p>
            <Alerta texto={e.errorBorrar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={e.cerrar}>
                Cancelar
              </Button>
              <Button variante="peligro" cargando={e.borrando} onClick={() => void e.confirmarBorrar()}>
                Sacar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>
    </>
  );
}

interface PropsEtiquetas {
  e: EdicionRubro;
  rubroId: number;
  nombre: string;
  etiquetas: string[];
}

/** Las etiquetas de la agenda que cubren el rubro: Badges (quitables para el admin) y un Combobox que elige o crea. */
export function EtiquetasRubro({ e, rubroId, nombre, etiquetas }: PropsEtiquetas) {
  return (
    <Stack espacio={2}>
      <h3 className="font-semibold">Etiquetas de proveedores</h3>
      <p className="text-chico text-texto-suave">Los presupuestos recibidos con estas etiquetas quedan vinculados al rubro.</p>
      {etiquetas.length > 0 ? (
        <Row envolver espacio={2}>
          {etiquetas.map((et) =>
            e.esAdmin ? (
              <Badge key={et} tono="primario" onQuitar={() => e.quitarEtiqueta(rubroId, etiquetas, et)} etiquetaQuitar={`Quitar etiqueta ${et}`}>
                {et}
              </Badge>
            ) : (
              <Badge key={et} tono="primario">
                {et}
              </Badge>
            ),
          )}
        </Row>
      ) : (
        <p className="text-chico text-texto-suave">Sin etiquetas.</p>
      )}
      {e.esAdmin && (
        <Combobox
          etiqueta={`Agregar etiqueta a ${nombre}`}
          opciones={e.opcionesEtiqueta(etiquetas)}
          onElegir={(texto) => e.sumarEtiqueta(rubroId, etiquetas, texto)}
          onCrear={(texto) => e.sumarEtiqueta(rubroId, etiquetas, texto)}
          placeholder="Ej.: carpintería de aluminio"
          deshabilitado={e.guardandoEtiquetas(rubroId)}
        />
      )}
      <Alerta texto={e.errorEtiqueta(rubroId)} />
    </Stack>
  );
}
